import { useRef, useState } from 'react';
import { formatDecimal, parseMoneyInput } from '../../lib/utils';

interface MoneyInputProps {
  /** Valor en format "1234.56" (punt decimal) o '' si està buit. */
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Decimals mostrats i arrodonits en sortir (per defecte 2). */
  decimals?: number;
  /** Si el camp tenia valor i s'esborra, en sortir queda a 0 (per defecte sí). Amb false queda buit (= "sense valor"). */
  zeroOnClear?: boolean;
}

/**
 * Camp d'import en €: en repòs mostra 1.234,56 (punt de milers, coma decimal,
 * 2 decimals arrodonits); en editar mostra el número net amb coma decimal.
 * Emet sempre un string amb punt decimal, compatible amb parseFloat.
 *
 * Si el camp tenia valor i l'usuari l'esborra, en sortir queda a 0 ("0,00"): un camp buit no s'envia
 * a l'API i el valor antic quedaria desat. Un camp que ja estava buit i no es toca continua buit.
 */
export function MoneyInput({ value, onValueChange, className, placeholder, disabled, decimals = 2, zeroOnClear = true }: MoneyInputProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const startedEmpty = useRef(true);

  const shown =
    draft !== null
      ? draft
      : value !== '' && !Number.isNaN(parseFloat(value))
        ? formatDecimal(parseFloat(value), decimals)
        : '';

  return (
    <input
      type="text"
      inputMode="decimal"
      value={shown}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      onFocus={() => {
        startedEmpty.current = value === '' || Number.isNaN(parseFloat(value));
        setDraft(value !== '' && !Number.isNaN(parseFloat(value)) ? String(parseFloat(value)).replace('.', ',') : '');
      }}
      onChange={(e) => {
        setDraft(e.target.value);
        onValueChange(parseMoneyInput(e.target.value));
      }}
      onBlur={() => {
        const n = parseFloat(parseMoneyInput(draft ?? ''));
        const factor = 10 ** decimals;
        onValueChange(Number.isNaN(n) ? (startedEmpty.current || !zeroOnClear ? '' : '0') : String(Math.round(n * factor) / factor));
        setDraft(null);
      }}
    />
  );
}
